import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { History, Phone, PhoneMissed } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from './ui/Button'
import { useArmedAction } from '../hooks/useArmedAction'
import { useCallbackList } from '../hooks/useCallbackList'
import { missedCallsApi, type CallbackItem } from '../services/missedCalls'
import { telephonyMissedCalls } from '../services/telephonyMissedCalls'
import { isTelephonyV2Enabled } from '../telephony/flags'
import { DismissButton } from './missed-calls/CallbackActions'
import { UnknownNumberButton } from './contacts/UnknownNumberButton'
import { UnknownNumberPanel } from './contacts/UnknownNumberPanel'
import {
  callbackContactName,
  formatCallbackNumber,
  formatRelativeTime,
} from './missed-calls/callbackFormat'
import { toastApiError } from '../utils/apiError'

/**
 * BADGE « À RAPPELER » de l'en-tête (#193).
 *
 * AVANT ce lot, le badge comptait les événements `call.missed` reçus par CE
 * navigateur AUJOURD'HUI (liste en localStorage, une ligne par appel) : un
 * agent connecté après le manqué ne voyait rien, un numéro rappelé par un
 * collègue restait affiché, et « Ignorer » n'effaçait que sa propre copie.
 *
 * DÉSORMAIS le compteur est celui de la liste SERVEUR « À rappeler »
 * (`GET /missed-calls`, 7 jours, une ligne par NUMÉRO, rattrapés et classés
 * exclus) — le même cache que le panneau du téléphone, donc toujours le même
 * chiffre. Le socket `call.missed` (S0-D, inchangé) ne sert plus qu'à deux
 * choses : prévenir l'agent (toast) et recharger la liste.
 *
 * Le rappel depuis le badge garde le geste historique : il OUVRE le téléphone
 * avec le numéro prérempli (`useRecallPrefill`) — c'est l'agent qui appuie sur
 * « Appeler ». « Historique » (#237) ouvre le panneau « Numéro inconnu » (PR #223)
 * sur le numéro BRUT de la ligne — sans appel ni préremplissage : l'agent lit ce
 * que le CRM sait du numéro avant de rappeler. « Ne pas rappeler » est le MÊME geste que dans le panneau du
 * téléphone : libellé visible, confirmation en place (`useArmedAction`).
 *
 * LOOKUP-UNIFIE (règle #396, `callbackContactName`) : une ligne dont le numéro
 * a une fiche NOMMÉE affiche le NOM SEUL — le numéro passe en infobulle et dans
 * les noms accessibles (« nom, numéro ») ; sans fiche nommée, le NUMÉRO SEUL.
 * Le toast d'un manqué dit le nom quand la liste fraîche en porte un
 * (`missedCalls.toastNamed`), le numéro sinon (`missedCalls.toast`, inchangée).
 * Le nom est saisi par un tiers : rendu par React (échappé), jamais journalisé.
 */

/** Lignes montrées dans le menu ; le reste se lit dans le téléphone. */
const DROPDOWN_MAX_ROWS = 8

/**
 * #382 — LA PASTILLE DE LA BARRE DU HAUT, À LA HAUTEUR DE SES VOISINES.
 *
 * Mesuré sur le harnais (CSS compilé) : la pastille faisait 44 px de haut
 * (`min-h-touch`) contre 32 px pour l'horloge (`HEADER_CHIP`, `h-8`) et
 * 34 px pour la cloche — elle cassait l'alignement de la barre. Le DESSIN
 * passe à 32 px (`h-8`), icône de 16 px comme la cloche ; la CIBLE reste de
 * 44 px par un pseudo-élément centré (même procédé que `IconButton` `sm`),
 * qui ne déborde qu'en hauteur : la pastille fait plus de 44 px de large, sa
 * cible ne mord donc pas sur la cloche voisine.
 */
const MISSED_BADGE_CLASS =
  'relative flex h-8 items-center gap-1.5 rounded-pill border border-danger-500/30 bg-danger-500/15 px-3 ' +
  'text-danger-400 transition-colors hover:bg-danger-500/25 ' +
  "before:absolute before:inset-x-0 before:top-1/2 before:h-touch before:-translate-y-1/2 before:content-['']"

export default function MissedCallsBadge() {
  const { t, i18n } = useTranslation('shell')
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  // #237 : numéro dont le panneau « Numéro inconnu » est ouvert (`null` = fermé).
  const [unknownPhone, setUnknownPhone] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const list = useCallbackList()
  const armed = useArmedAction()

  // Socket v2 (source unique depuis LOT-SUNSET-2) : la connexion suit le flag
  // — opt-out `telephony_v2 = 'false'` ⇒ aucun poste, donc aucune socket.
  // `t` est lu par ref : son identité (changement de langue) ne doit JAMAIS
  // couper puis rouvrir la socket — un manqué tomberait dans l'intervalle.
  const translate = useRef(t)
  translate.current = t
  // #193b : le socket annonce TOUS les manqués du tenant ; un agent ne doit
  // être prévenu que des siens et des non attribués. On demande donc au
  // serveur (qui borne la liste à ce que je vois) et on n'annonce que si le
  // numéro y figure. Un manqué en numéro masqué n'y figure jamais : pas de toast.
  // ⚠️ Requête DIRECTE, hors cache React Query : un `fetchQuery` sur la clé de
  // la liste est ANNULÉ par l'invalidation que le même événement déclenche
  // (CancelledError avalée → plus aucun toast, vu sur le banc).
  useEffect(() => {
    const announce = (call: { callerNumber: string }) => {
      void missedCallsApi
        .list('all')
        .then((fresh) => {
          const seen = fresh.items.find((item) => item.number === call.callerNumber)
          if (!seen) return
          // LOOKUP-UNIFIE : le nom de la fiche quand le serveur en rend un.
          const name = callbackContactName(seen)
          toast(
            name
              ? translate.current('missedCalls.toastNamed', { name })
              : translate.current('missedCalls.toast', {
                  number: formatCallbackNumber(call.callerNumber),
                }),
            { duration: 5000, icon: '📞' },
          )
        })
        .catch(() => undefined)
    }
    if (isTelephonyV2Enabled()) {
      telephonyMissedCalls.connect()
      telephonyMissedCalls.onMissedCall(announce)
    }
    return () => {
      telephonyMissedCalls.offMissedCall(announce)
      telephonyMissedCalls.disconnect()
    }
  }, [])

  // Ferme le menu au clic extérieur.
  useEffect(() => {
    if (!isOpen) return
    const onMouseDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [isOpen])

  const total = list.data?.counts.total ?? 0
  // Le panneau s'ouvre sous le nom que la ligne affichait (règle #396).
  const unknownName = unknownPhone
    ? callbackContactName(list.data?.items.find((entry) => entry.number === unknownPhone) ?? {})
    : null
  const panel = (
    <UnknownNumberPanel
      phone={unknownPhone}
      contactName={unknownName}
      onClose={() => setUnknownPhone(null)}
    />
  )
  // Le panneau survit à un total tombé à 0 (dernier numéro classé depuis lui).
  if (total === 0) return panel

  const items = (list.data?.items ?? []).slice(0, DROPDOWN_MAX_ROWS)

  const openPhone = (item?: CallbackItem) => {
    setIsOpen(false)
    // Forme harmonisée `{ phoneNumber, contactName }` (`useRecallPrefill`) :
    // la scène préremplit le composeur, l'agent décide quand appeler.
    // LOOKUP-UNIFIE : le composeur s'ouvre sous le nom de la fiche s'il y en a un.
    navigate(
      '/v2/softphone',
      item
        ? {
            state: {
              phoneNumber: item.number,
              contactName: callbackContactName(item) ?? item.number,
            },
          }
        : undefined,
    )
  }

  const dismiss = async (item: CallbackItem) => {
    try {
      await list.dismiss(item)
      // La ligne disait le nom — le toast aussi (même règle que le panneau).
      toast.success(
        t('missedCalls.dismissed', {
          number: callbackContactName(item) ?? formatCallbackNumber(item.number),
        }),
      )
    } catch (error) {
      toastApiError(error, t('missedCalls.dismissFailed'))
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={t('missedCalls.badgeLabel', { count: total })}
        className={MISSED_BADGE_CLASS}
      >
        <PhoneMissed className="h-4 w-4" aria-hidden="true" />
        <span className="text-t5 font-semibold tabular-nums">{total}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-border-secondary bg-background-primary shadow-xl">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <PhoneMissed className="h-4 w-4 shrink-0 text-danger-400" aria-hidden="true" />
            <span className="text-t4 font-semibold text-foreground">
              {t('missedCalls.title')}
            </span>
            <span className="text-t5 tabular-nums text-foreground-secondary">{total}</span>
          </div>

          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {items.map((item) => {
              const number = formatCallbackNumber(item.number)
              // Règle #396 : le NOM SEUL quand il existe, le numéro seul sinon ;
              // `spoken` = ce que lit un lecteur d'écran (« nom, numéro »).
              const name = callbackContactName(item)
              const spoken = name ? `${name}, ${number}` : number
              return (
                <li key={item.number} className="flex flex-col items-stretch pb-1">
                  <button
                    type="button"
                    onClick={() => openPhone(item)}
                    aria-label={t('missedCalls.prefill', { number: spoken })}
                    title={name ? number : undefined}
                    className="flex min-h-touch min-w-0 flex-1 items-center gap-3 px-4 py-2 text-left hover:bg-background-secondary"
                  >
                    <Phone className="h-4 w-4 shrink-0 text-foreground-secondary" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      {name ? (
                        <span
                          data-testid="missed-contact-name"
                          className="block truncate text-t4 font-semibold text-foreground"
                        >
                          {name}
                        </span>
                      ) : (
                        <span className="block truncate text-t4 font-medium tabular-nums text-foreground">
                          {number}
                        </span>
                      )}
                      <span className="block truncate text-t6 text-foreground-secondary">
                        {t('missedCalls.missedCalls', { count: item.attempts })}
                        {' · '}
                        {formatRelativeTime(item.lastMissedAt, i18n.language)}
                        {item.mine && ` · ${t('missedCalls.yourContact')}`}
                        {item.unassigned && ` · ${t('missedCalls.unassigned')}`}
                        {!item.mine &&
                          item.lastAgent &&
                          ` · ${item.lastAgent.name ?? ''}`.trimEnd()}
                      </span>
                      {item.callbackTries > 0 && (
                        <span className="block truncate text-t6 text-warning-400">
                          {t('missedCalls.triedNoAnswer', { count: item.callbackTries })}
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="flex items-center justify-between gap-2 px-2">
                    <UnknownNumberButton
                      phone={item.number}
                      label={number}
                      name={name}
                      onOpen={(phone) => {
                        setIsOpen(false)
                        setUnknownPhone(phone)
                      }}
                      className="gap-1.5 px-2 text-t5 text-foreground-secondary"
                    >
                      <History className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {t('missedCalls.history')}
                    </UnknownNumberButton>
                    <DismissButton
                      number={item.number}
                      armed={armed}
                      size="sm"
                      label={t('missedCalls.doNotCallBack')}
                      confirmLabel={t('missedCalls.confirmDismiss')}
                      ariaLabel={t('missedCalls.dismiss', { number: spoken })}
                      disabled={list.dismissingNumber === item.number}
                      onConfirm={() => void dismiss(item)}
                    />
                  </div>
                </li>
              )
            })}
          </ul>

          {(list.data?.counts.hidden ?? 0) > 0 && (
            <p className="border-t border-border px-4 py-2 text-t6 text-foreground-secondary">
              {t('missedCalls.hidden', { count: list.data?.counts.hidden ?? 0 })}
            </p>
          )}

          <div className="border-t border-border p-2">
            <Button variant="ghost" fullWidth onClick={() => openPhone()}>
              {total > items.length
                ? t('missedCalls.openPhoneAll', { count: total })
                : t('missedCalls.openPhone')}
            </Button>
          </div>
        </div>
      )}
      {panel}
    </div>
  )
}
